import { HEROES, SKILLS, type SkillId } from '@somnhold/content';
import { kitOf, type BattleState, type Hero } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { isTouch } from '../settings.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/** What the coach can point at on the board. */
export type PinTarget = 'keep' | 'gate' | 'hero';
/** Where the camera goes for a step: the whole sector, or close on a spot. */
export type CoachView = 'overview' | 'keep' | 'gate' | 'build' | 'hero';
export type CoachAction = 'move' | 'start';

interface Step {
  text: () => string;
  /** Extra content under the text (a legend). */
  extra?: () => HTMLElement;
  /** HUD elements to highlight (selectors inside the screen). */
  focus?: string[];
  pin?: PinTarget;
  view?: CoachView;
  /** The step ends by itself when this becomes true; otherwise only "Next" moves on. */
  done?: (s: BattleState, acts: Set<CoachAction>) => boolean;
  /** Hide "Next" until `done` (the player has to do it). */
  must?: boolean;
}

const KEY = 'somnhold.tutorial';
export const tutorialDone = (): boolean => {
  try {
    return localStorage.getItem(KEY) === 'done';
  } catch {
    return false;
  }
};
const markDone = (): void => {
  try {
    localStorage.setItem(KEY, 'done');
  } catch {
    /* storage blocked */
  }
};

const built = (s: BattleState, type?: string): number => s.structures.filter((x) => x.type !== 'gate' && (!type || x.type === type)).length;

/**
 * Practice-field guide: walks a new player through the keep, the gate, resources, building, the hero
 * and the skills, then drops short tips during the wave. Skippable; remembered once finished.
 */
export class Coach {
  readonly el: HTMLElement;
  readonly pin: HTMLElement;
  private steps: Step[];
  private i = -1;
  private acts = new Set<CoachAction>();
  private focused: HTMLElement[] = [];
  private textEl: HTMLElement;
  private extraEl: HTMLElement = h('div');
  private countEl: HTMLElement;
  private nextBtn: HTMLButtonElement;
  private skipBtn: HTMLButtonElement;
  private guided = false;
  private tipTimer = 0;
  private tipsSeen = new Set<string>();
  private baseBuilt = 0;

  constructor(
    private root: HTMLElement,
    private hero: () => Hero | undefined,
    private onStart: () => void,
    private onView: (v: CoachView) => void = () => undefined,
  ) {
    this.textEl = h('p.coach-text');
    this.countEl = h('span.coach-count');
    this.nextBtn = h('button.btn.small', { 'data-test': 'coach-next', onclick: () => this.next() }, t('coach.next')) as HTMLButtonElement;
    this.skipBtn = h('button.btn.ghost.small', { 'data-test': 'coach-skip', onclick: () => this.finish() }, t('coach.skip')) as HTMLButtonElement;
    this.el = h('div.coach', { role: 'status', 'aria-live': 'polite' }, h('div.coach-head', {}, h('span.coach-title', {}, t('coach.title')), this.countEl), this.textEl, this.extraEl, h('div.coach-actions', {}, this.skipBtn, this.nextBtn));
    this.pin = h('div.coach-pin', {}, h('i'));
    const touch = isTouch();
    const skillLine = (i: number): string => {
      const me = this.hero();
      const id = (me ? kitOf(me) : HEROES.warrior.skills)[i] as SkillId;
      return `${t(`skill.${id}`)}: ${t(`skill.${id}.desc`, { n: SKILLS[id].amount, s: SKILLS[id].duration / 12 })}`;
    };
    this.steps = [
      { text: () => t('coach.welcome'), view: 'overview' },
      { text: () => t('coach.keep'), focus: ['.keep-card'], pin: 'keep', view: 'keep' },
      { text: () => t('coach.gate'), pin: 'gate', view: 'gate' },
      {
        view: 'overview',
        text: () => t('coach.res'),
        focus: ['.res'],
        extra: () => h('ul.coach-legend', {}, ...(['wood', 'stone', 'crystal'] as const).map((k) => h('li', {}, h('span.ic', { html: ICONS[k] }), h('span', {}, h('b', {}, t(`res.${k}`)), ` — ${t(`coach.res.${k}`)}`)))),
      },
      {
        text: () => t(touch ? 'coach.build.touch' : 'coach.build.pc'),
        focus: ['[data-build="ballista"]'],
        pin: 'gate',
        view: 'build',
        must: true,
        done: (s) => built(s, 'ballista') > 0,
      },
      {
        text: () => `${t('coach.more')}\n${t('structure.catapult')}: ${t('structure.catapult.desc')}\n${t('structure.arcane')}: ${t('structure.arcane.desc')}`,
        focus: ['[data-build="catapult"]', '[data-build="arcane"]'],
        view: 'build',
        done: (s) => built(s) > this.baseBuilt,
      },
      { text: () => t(touch ? 'coach.hero.touch' : 'coach.hero.pc'), focus: ['.hero-card'], pin: 'hero', view: 'hero', done: (_s, a) => a.has('move') },
      { text: () => `${t('coach.skills')}\n${skillLine(0)}\n${skillLine(1)}\n${skillLine(2)}`, focus: ['.skills'], view: 'hero' },
      { text: () => t(touch ? 'coach.camera.touch' : 'coach.camera.pc'), view: 'overview' },
      { text: () => t('coach.start'), view: 'overview' },
    ];
  }

  private next(): void {
    // The last step's button calls the wave.
    if (this.i === this.steps.length - 1) {
      this.finish();
      this.onStart();
      return;
    }
    this.go(this.i + 1);
  }

  get active(): boolean {
    return this.guided;
  }

  /** Starts (or restarts) the guided steps. */
  begin(): void {
    this.guided = true;
    this.root.classList.add('coached');
    this.go(0);
  }

  event(a: CoachAction): void {
    this.acts.add(a);
  }

  private go(n: number): void {
    this.acts.clear();
    for (const f of this.focused) f.classList.remove('coach-focus');
    this.focused = [];
    if (n >= this.steps.length) {
      this.finish();
      return;
    }
    this.i = n;
    const st = this.steps[n] as Step;
    this.textEl.textContent = st.text();
    this.extraEl.replaceChildren(...(st.extra ? [st.extra()] : []));
    this.countEl.textContent = `${n + 1} / ${this.steps.length}`;
    this.nextBtn.textContent = n === 0 ? t('coach.begin') : n === this.steps.length - 1 ? t('hud.start') : t('coach.next');
    this.nextBtn.hidden = !!st.must;
    this.skipBtn.hidden = false;
    for (const sel of st.focus ?? []) {
      for (const el of this.root.querySelectorAll<HTMLElement>(sel)) {
        el.classList.add('coach-focus');
        this.focused.push(el);
      }
    }
    this.el.classList.add('show');
    if (st.view) this.onView(st.view);
  }

  private finish(): void {
    // Leaving the guide mid-way puts the table back on the whole sector.
    if (this.guided && this.i > 0) this.onView('overview');
    this.guided = false;
    this.root.classList.remove('coached');
    for (const f of this.focused) f.classList.remove('coach-focus');
    this.focused = [];
    this.i = -1;
    this.el.classList.remove('show');
    markDone();
  }

  /** A short tip during the wave, once each. */
  private tip(key: string, vars?: Record<string, string>): void {
    if (this.tipsSeen.has(key) || this.guided) return;
    this.tipsSeen.add(key);
    this.textEl.textContent = t(key, vars);
    this.extraEl.replaceChildren();
    this.countEl.textContent = '';
    this.nextBtn.hidden = true;
    this.skipBtn.hidden = true;
    this.el.classList.add('show', 'tip');
    window.clearTimeout(this.tipTimer);
    this.tipTimer = window.setTimeout(() => this.el.classList.remove('show', 'tip'), 6500);
  }

  /** Current board target for the pin, if any. */
  pinTarget(): PinTarget | null {
    if (!this.guided) return null;
    return this.steps[this.i]?.pin ?? null;
  }

  /** Called once per simulation tick. */
  update(s: BattleState, tipsOn: boolean): void {
    if (this.guided) {
      const st = this.steps[this.i];
      if (st?.done?.(s, this.acts)) {
        if (this.i === 4) this.baseBuilt = built(s);
        this.go(this.i + 1);
      }
      // Starting the wave early ends the guided part.
      if (this.guided && s.started) this.finish();
      return;
    }
    if (!tipsOn || !s.started || s.result !== 'running') return;
    const me = this.hero();
    if (s.waveT > 48 && s.wave.next > 0) this.tip('coach.tip.spawn');
    if (s.keep.hp < s.keep.maxHp) this.tip('coach.tip.keep');
    if (me && me.downTicks > 0) this.tip('coach.tip.down');
    if (me && s.waveT > 600 && me.skillCd[2] === 0 && me.downTicks === 0) {
      const id = kitOf(me)[2] as string;
      this.tip('coach.tip.ult', { name: t(`skill.${id}`) });
    }
  }

  destroy(): void {
    window.clearTimeout(this.tipTimer);
  }
}
